import { Box, Chip, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material"
import { Done } from "lib/@components/done"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/if"
import { PreviewField } from "./preview-field"
import { PreviewTaskRow } from "./preview-task-row"
import { buildPreviewTasks, indexAcceptedChangesForPreview } from "./resolution-preview-helpers"

/**
 * Full-schedule preview dialog that walks the schedule intro and all tasks,
 * rendering character-level diffs inline for every field where an accepted
 * change exists.
 *
 * Opened via `useDialog(<ResolutionPreviewDialog />, ...)` — receives
 * `{ resolution }` from the caller.
 */
export function ResolutionPreviewDialog({ resolution }) {
    if (!resolution) return null

    const acceptedChanges = (resolution.proposedChanges ?? []).filter((c) => c.decision === "accept")
    const changesByType = indexAcceptedChangesForPreview(acceptedChanges, resolution.tasks)
    const scheduleTitleChange = changesByType.schedule_title ?? null

    const intro = resolution.intro ?? {}
    const introContent = changesByType.intro_content?.newText ?? null
    const introNote = changesByType.intro_note?.newText ?? null
    const previewTitle = scheduleTitleChange?.newText ?? resolution.title

    return (
        <>
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6">
                        {resolution.code} — {previewTitle}
                    </Typography>
                    <Chip label="Preview with accepted changes" size="small" color="info" variant="outlined" />
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} pb={4}>
                    <If truthy={scheduleTitleChange}>
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                                Schedule Title
                            </Typography>
                            <PreviewField
                                label="Title"
                                oldText={scheduleTitleChange ? scheduleTitleChange.oldText : resolution.title}
                                newText={previewTitle}
                                hasChange={!!scheduleTitleChange}
                            />
                        </Box>
                        <Divider />
                    </If>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                            Legend
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        bgcolor: "success.lighter",
                                        border: "1px solid",
                                        borderColor: "success.main",
                                        borderRadius: 0.5,
                                    }}
                                />
                                <Typography variant="caption">Added text</Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        bgcolor: "error.lighter",
                                        border: "1px solid",
                                        borderColor: "error.main",
                                        borderRadius: 0.5,
                                    }}
                                />
                                <Typography variant="caption">Removed text</Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        bgcolor: "action.hover",
                                        border: "1px solid",
                                        borderColor: "action.disabled",
                                        borderRadius: 0.5,
                                    }}
                                />
                                <Typography variant="caption">Modified text</Typography>
                            </Stack>
                        </Stack>
                    </Box>

                    <Divider />

                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                            Introduction
                        </Typography>
                        <Stack spacing={1}>
                            <If truthy={introContent !== null || intro.content}>
                                <PreviewField
                                    label="Content"
                                    oldText={
                                        changesByType.intro_content
                                            ? changesByType.intro_content.oldText
                                            : intro.content
                                    }
                                    newText={introContent ?? intro.content}
                                    hasChange={introContent !== null}
                                />
                            </If>
                            <If truthy={introNote !== null || intro.note}>
                                <PreviewField
                                    label="Note"
                                    oldText={changesByType.intro_note ? changesByType.intro_note.oldText : intro.note}
                                    newText={introNote ?? intro.note}
                                    hasChange={introNote !== null}
                                />
                            </If>
                        </Stack>
                    </Box>

                    <Divider />

                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                            Tasks
                        </Typography>
                        <Repeat
                            list={buildPreviewTasks(resolution, changesByType)}
                            item={<PreviewTaskRow />}
                            keyFn={(t) => t.id ?? t.taskId ?? `preview-${t.position}`}
                        />
                    </Box>
                </Stack>
            </DialogContent>
            <Done />
        </>
    )
}
